/**
 * Suivi des enveloppes budgetaires.
 *
 * Un budget se mesure en **charge reelle** : la facture de dentiste avancee
 * pour 400.00 ne consomme que ma quote-part de l'enveloppe Sante. Compter le
 * total ferait exploser le budget a chaque avance de frais, pour de l'argent
 * qui reviendra.
 */

import { somme } from '../domain/argent'
import {
  anneeDe,
  dateDeDayKey,
  dernierJourDuMois,
  joursDansMois,
  premierJourDuMois,
} from '../domain/temps'
import type { Budget, Cents, DayKey, Id, MoisKey } from '../domain/types'
import { chargeReelle, perimetreCategorie, type Index } from './donnees'

export function budgetEnVigueur(b: Budget, mois: MoisKey): boolean {
  return b.debut <= mois && (b.fin === null || b.fin >= mois)
}

export type EtatBudget = 'ok' | 'attention' | 'depasse'

export interface SuiviBudget {
  budget: Budget
  du: DayKey
  au: DayKey
  plafond: Cents
  consomme: Cents
  restant: Cents
  /** Part consommee, 0-1 (peut depasser 1). */
  taux: number
  /** Part de la periode deja ecoulee, 0-1. Comparer `taux` a `ecoule` dit si
   *  l'on depense plus vite que le calendrier : 60 % consomme le 10 du mois
   *  n'est pas la meme chose que 60 % consomme le 25. */
  ecoule: number
  etat: EtatBudget
}

/**
 * Consommation d'un budget sur sa periode contenant `mois`.
 *
 * Un budget annuel se suit sur l'annee civile entiere : une enveloppe
 * « Vacances » de 3'000.- n'a pas de sens decoupee en douziemes.
 */
export function suivreBudget(ix: Index, b: Budget, mois: MoisKey, aujourdhui: DayKey): SuiviBudget {
  const du = b.periode === 'mensuel' ? premierJourDuMois(mois) : `${anneeDe(mois)}-01-01`
  const au = b.periode === 'mensuel' ? dernierJourDuMois(mois) : `${anneeDe(mois)}-12-31`
  const perimetre = perimetreCategorie(ix, b.categorieId)

  const consomme = somme(
    ix.d.operations
      .filter(
        (op) =>
          op.sens === 'depense' &&
          op.date >= du &&
          op.date <= au &&
          perimetre.has(op.categorieId) &&
          (b.compteId === null || op.compteId === b.compteId),
      )
      .map((op) => chargeReelle(ix, op)),
  )

  const ecoule = fractionEcoulee(du, au, aujourdhui)
  const taux = b.montant > 0 ? consomme / b.montant : consomme > 0 ? Infinity : 0

  let etat: EtatBudget = 'ok'
  if (consomme > b.montant) etat = 'depasse'
  // Alerte quand on consomme nettement plus vite que le temps ne passe, mais
  // pas sur les tout premiers jours, ou un seul achat fausse le rythme.
  else if (taux >= 0.9 || (ecoule > 0.15 && ecoule < 1 && taux > ecoule * 1.25)) etat = 'attention'

  return { budget: b, du, au, plafond: b.montant, consomme, restant: b.montant - consomme, taux, ecoule, etat }
}

function fractionEcoulee(du: DayKey, au: DayKey, aujourdhui: DayKey): number {
  if (aujourdhui < du) return 0
  if (aujourdhui > au) return 1
  const jour = (k: DayKey) => {
    const d = dateDeDayKey(k)
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  }
  const total = (jour(au) - jour(du)) / 86_400_000 + 1
  const passes = (jour(aujourdhui) - jour(du)) / 86_400_000 + 1
  return passes / total
}

/** Budgets en vigueur pour le mois, filtres par compte (`null` : tous). */
export function suivreBudgets(
  ix: Index,
  mois: MoisKey,
  aujourdhui: DayKey,
  compteId: Id | null,
): SuiviBudget[] {
  return ix.d.budgets
    .filter((b) => budgetEnVigueur(b, mois) && (compteId === null || b.compteId === null || b.compteId === compteId))
    .map((b) => suivreBudget(ix, b, mois, aujourdhui))
}

/** Utile aux tests et aux libelles : nombre de jours du mois. */
export function joursDuMois(mois: MoisKey): number {
  const [y, m] = mois.split('-').map(Number)
  return joursDansMois(y ?? 1970, m ?? 1)
}
